import React, { FC, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import axios, { AxiosError } from 'axios';
import { useProfessions } from '../../hooks/useProfessions';
import { ProfessionListItem } from './ProfessionListItem';
import { IProfession } from '../../models/Profession.interface';
import { ProfessionsEndpoint } from '../../api/endpoints';
import axiosInstant from '../../api';
import { MainContainer } from '../../components/ContentWrapper';
import { DotLottleLoader } from '../../components/Loader';
import { PageTitle } from '../../components/PageTitle';
import { IconAdd, IconConfirm, IconClose } from '../../utils/icons';
import { ActionIconButton, InlineInput } from './constants';

export const ProfessionsPage: FC = () => {
  const { professionsList, subProfessionLibrary, getProfessions, areProfessionsLoaded } = useProfessions();
  const [newOne, setNewOne] = useState<number | undefined>();
  const [professions, setProfessions] = useState<Array<IProfession>>(professionsList);
  const [initialLoaded, setInitialLoaded] = useState<boolean>(professionsList.length > 0);
  const [addingParent, setAddingParent] = useState(false);
  const [newParent, setNewParent] = useState('');

  useEffect(() => {
    if (!areProfessionsLoaded) getProfessions();
  }, [areProfessionsLoaded]);

  useEffect(() => {
    setProfessions(professionsList);
    if (professionsList.length > 0 || areProfessionsLoaded) {
      setInitialLoaded(true);
    }
  }, [professionsList, areProfessionsLoaded]);

  const totalSubs = professions.reduce(
    (acc, p) => acc + (subProfessionLibrary[p.id]?.length ?? 0),
    0,
  );

  const handleProfessionUpdate = (professionId: number, newItem: IProfession | null) => {
    const index = professions.findIndex((sp) => sp.id === professionId);
    if (newItem === null) {
      professions.splice(index, 1);
    } else {
      professions[index] = newItem;
      professions.sort((a, b) => a.name.localeCompare(b.name));
    }
    setProfessions([...professions]);
    getProfessions();
  };

  const handleAddProfession = async () => {
    const name = newParent.trim();
    if (!name) return;
    try {
      const response = await axiosInstant.post(ProfessionsEndpoint(), { name });
      const { data } = response;
      getProfessions();
      setNewOne(data.id);
      professions.push(data);
      professions.sort((a, b) => {
        if (a.order === b.order) return a.name.localeCompare(b.name);
        return a.order - b.order;
      });
      setProfessions([...professions]);
      setNewParent('');
      setAddingParent(false);
      toast.success(`Profession '${name}' created!`);
    } catch (e) {
      const error = e as AxiosError;
      if (axios.isAxiosError(error)) {
        console.error(error.response?.data);
        toast.error(error.response?.data as string);
      } else {
        console.error(error);
        toast.error('Failed to create new profession');
      }
    }
  };

  const handleCancelAddParent = () => {
    setAddingParent(false);
    setNewParent('');
  };

  if (!initialLoaded) {
    return (
      <MainContainer>
        <DotLottleLoader fill={false} />
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <PageTitle title="Professions" />
          <p className="text-[13px] text-text-2 mt-1">
            {professions.length} categories · {totalSubs} sub-professions
          </p>
        </div>

        {!addingParent && (
          <button
            type="button"
            onClick={() => setAddingParent(true)}
            className="flex items-center gap-1 bg-accent text-sm sm:text-base text-white py-2.5 sm:py-3 px-4 sm:px-5 lg:px-10 cursor-pointer rounded-xl focus:outline-none focus:shadow-outline whitespace-nowrap shrink-0"
          >
            <IconAdd stroke="#FFF" />
            Add profession
          </button>
        )}
      </div>

      {addingParent && (
        <div className="flex items-center gap-2 pb-4">
          <InlineInput
            autoFocus
            placeholder="Profession name…"
            value={newParent}
            onChange={(e) => setNewParent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddProfession();
              if (e.key === 'Escape') handleCancelAddParent();
            }}
          />
          <div className="flex items-center gap-0.5 shrink-0">
            <ActionIconButton tone="confirm" title="Save" onClick={handleAddProfession}>
              <IconConfirm />
            </ActionIconButton>
            <ActionIconButton title="Cancel" onClick={handleCancelAddParent}>
              <IconClose />
            </ActionIconButton>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 pb-6">
        {professions.map((profession) => (
          <ProfessionListItem
            key={profession.id}
            profession={profession}
            onUpdate={handleProfessionUpdate}
            isNew={newOne === profession.id}
          />
        ))}
      </div>
    </MainContainer>
  );
};
